import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import i18n from '@nextroll/ar-i18n';
import { Modal } from '@nextroll/ar-style-base';

import useRequest from '../../../hooks/useRequest';

interface IProps {
    show: boolean;
    title?: string;
    description?: string;
    confirmationText?: string; // confirmation text e.g. "DELETE"

    request?: () => void;
    onClose?: () => void;

    isReverseActions?: boolean; // show the confirm button first
    isDangerColor?: boolean; // red confirm button
    btnConfirmLabel?: string; // overwrite confirm button text
    btnCancelLabel?: string; // overwrite cancel button text

    onSuccess?: () => void;
}

export const ConfirmDialog = (props: IProps) => {
    const [call, loading] = useRequest();

    const [confirmInput, setConfirmInput] = useState('');

    /**
     * Determine if there is a mismatch in the confirmation text
     */
    const confirmationInputError = useMemo(
        () => !!confirmInput && confirmInput !== props.confirmationText,
        [confirmInput, props.confirmationText],
    );

    /**
     * Input for the optional confirmation text, e.g. "DELETE"
     */
    const confirmationInput = useMemo(() => props.confirmationText && (
        <>
            <label className='control-label'>
                {i18n.gettext(
                    'Type "{{confirmationText}}" to confirm deletion',
                    { confirmationText: props.confirmationText },
                )}
            </label>
            <input
                data-testid='workflow-confirm-dialog-input'
                type='text'
                value={confirmInput}
                className={`form-control${confirmationInputError ? ' error' : ''}`}
                onChange={e => setConfirmInput(e.target.value)}
            />
            <p className='text-danger'>{confirmationInputError && i18n.gettext('Confirmation is required.')}</p>
        </>
    ), [props.confirmationText, confirmInput, confirmationInputError]);

    /**
     * Determine if it is waiting for the user input of the confirmationt text. If so, then do not proceed.
     */
    const isWaitingForConfirmation = useMemo(
        () => !!props.confirmationText && (!confirmInput || confirmationInputError),
        [props.confirmationText, confirmInput, confirmationInputError],
    );

    /**
     * Make sure to clear the confirmation input when the dialog is closed
     */
    const handleClose = () => {
        if (loading) {
            return; // still loading; do not dismiss
        }
        confirmInput && setConfirmInput('');
        props.onClose?.();
    };

    /**
     * Send out the request and start the loading spinner
     */
    const handleConfirm = async () => {
        if (props.request) {
            await call(props.request);
            await props.onSuccess?.(); // success callback
        }
        handleClose();
    };

    return (
        <Modal show={props.show} size={Modal.SIZE_MEDIUM} animate onHide={handleClose} className='workflow-confirm-dialog'>
            <div data-testid='workflow-confirm-dialog' className='ar-card'>
                <div className='ar-card-header'>
                    <div className='ar-card-title'>{props.title}</div>
                    <button type='button' className='btn close' onClick={handleClose} />
                </div>
                <div className='ar-card-body'>
                    <p>{props.description}</p>
                    {confirmationInput}
                </div>
                <div className='ar-card-footer'>
                    <div className='ar-card-actions' style={{ flexDirection: props.isReverseActions ? 'row-reverse' : 'row' }}>
                        <button
                            type='button'
                            role='button'
                            disabled={loading}
                            className={classNames('btn', {
                                'btn-primary': !!props.isReverseActions,
                                'btn-default': !props.isReverseActions,
                            })}
                            onClick={handleClose}
                        >
                            {props.btnCancelLabel || i18n.gettext('Cancel')}
                        </button>
                        <button
                            type='button'
                            role='button'
                            disabled={loading || isWaitingForConfirmation}
                            className={classNames('btn', {
                                'btn-danger': !!props.isDangerColor,
                                'btn-primary': !props.isReverseActions && !props.isDangerColor,
                                'btn-default': !!props.isReverseActions,
                            })}
                            onClick={handleConfirm}
                        >
                            {props.btnConfirmLabel || i18n.gettext('Confirm')}
                            {loading && <span className='loading-spinner-small' />}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
